import React from 'react';
import { createSafeHTML } from './HTMLSanitizer';

/**
 * Utilitaire partagé pour rendre une description avec photos intégrées
 * Utilisé par PlacePage et HikingPage
 * 
 * @param {string} description - Description avec placeholders photo (%1%, %2%, etc)
 * @param {Array} photos - Tableau des photos
 * @param {Function} onPhotoClick - Callback appelé au click sur une photo (globalIndex, sortedPhotos)
 * @param {Object} cssClasses - Noms des classes CSS (rowClass, itemClass, photoClass)
 * @returns {React.ReactElement} - Contenu rendu
 */
export const renderDescriptionWithPhotos = (description, photos, onPhotoClick, cssClasses = {}) => {
    // Classes CSS par défaut (pour places)
    const {
        rowClass = 'place-photos-row',
        itemClass = 'place-photo-item',
        photoClass = 'place-description-photo',
        textClass = 'place-text-content'
    } = cssClasses;
    if (!description || !photos) return <p>{description}</p>;

    // Trier les photos par ordre et créer un map indexé de 1 à N
    const sortedPhotos = photos.sort((a, b) => a.order - b.order);
    const photosByIndex = {};
    sortedPhotos.forEach((photo, index) => {
        photosByIndex[index + 1] = photo.url;
    });

    // Utiliser une regex plus précise pour capturer les groupes de photos
    const photoGroupRegex = /(%\d+%(?:\s+%\d+%)*)/g;
    const segments = description.split(photoGroupRegex);
    
    const groupedElements = [];
    let globalPhotoIndex = 0;
    
    segments.forEach((segment, index) => {
        if (segment.match(/%\d+%/)) {
            // C'est un groupe de photos consécutives
            const photoMatches = segment.match(/%(\d+)%/g);
            const photosInGroup = [];
            
            photoMatches.forEach(match => {
                const photoIndex = parseInt(match.replace(/%/g, ''));
                const photoUrl = photosByIndex[photoIndex];
                
                if (photoUrl) {
                    photosInGroup.push({
                        photoIndex,
                        photoUrl,
                        globalIndex: globalPhotoIndex
                    });
                    globalPhotoIndex++;
                }
            });
            
            if (photosInGroup.length > 0) {
                groupedElements.push({
                    type: 'photos',
                    photos: photosInGroup,
                    key: `photos-${index}`
                });
            }
        } else if (segment.trim() !== '') {
            // C'est du texte normal
            groupedElements.push({
                type: 'text',
                content: segment,
                key: `text-${index}`
            });
        }
    });

    return (
        <div>
            {groupedElements.map(element => {
                if (element.type === 'photos') {
                    return (
                        <div 
                            key={element.key} 
                            className={`${rowClass} ${element.photos.length === 1 ? 'single-photo' : ''}`}
                        >
                            {element.photos.map(photo => (
                                <div className={itemClass} key={photo.globalIndex}>
                                    <img 
                                        src={photo.photoUrl} 
                                        alt={`Photo ${photo.photoIndex}`} 
                                        className={`${photoClass} clickable-photo`}
                                        loading="lazy"
                                        onClick={() => onPhotoClick(photo.globalIndex, sortedPhotos)}
                                        style={{cursor: 'pointer'}}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                } else {
                    return (
                        <div 
                            key={element.key} 
                            dangerouslySetInnerHTML={createSafeHTML(element.content)}
                            className={textClass}
                        />
                    );
                }
            })}
        </div>
    );
};
